import { useCallback } from 'react'
import {
  deleteLeadOppProduct,
  removeLeadOppProduct,
} from '@user/store/leadOppProducts'
import {
  decrementLeadServicesCount,
  getLeadDetail,
  getLeadValue,
  updateDeletedLeadAgreementsCount,
} from '@user/store/lead'
import {
  decrementOppServicesCount,
  getOpportunityDetail,
  getOpportunityValue,
} from '@user/store/opportunity'
import { DialogState, ServiceCardProps } from '../typings'
import { useAppDispatch } from '@user/store/hooks'
import { ENTITY_TYPES, updateLoadingState } from '@sales-center/common'
import { UseData } from './useData'
import { toast } from '@sales-center/components'

type Props = Pick<
  ServiceCardProps,
  'service' | 'onEditClick' | 'disableDelete' | 'entityType' | 'localMode'
> &
  Pick<UseData, 'setDetailsOpen'> & {
    detailId?: number
    setDialog: (state?: DialogState) => void
  }

export function useHandlers(props: Props) {
  const {
    service,
    setDetailsOpen,
    detailId,
    localMode,
    setDialog,
    entityType,
    onEditClick,
    disableDelete,
  } = props

  const { id, entityId } = service

  const dispatch = useAppDispatch()

  const handleAccordionChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, expanded: boolean) => {
      setDetailsOpen(expanded)
    },
    [setDetailsOpen],
  )

  const handleEditClick = useCallback(
    () => onEditClick(service),
    [onEditClick, service],
  )

  const handleDeleteClick = useCallback(async () => {
    dispatch(updateLoadingState('visible'))
    const result = localMode
      ? dispatch(
          removeLeadOppProduct({
            id: id,
            draftAgreementsUpdated: false,
            deletedDraftAgreementsCount: 0,
          }),
        )
      : await dispatch(deleteLeadOppProduct({ id, entityType, entityId }))

    if (!!entityId) {
      if (deleteLeadOppProduct.fulfilled.match(result)) {
        dispatch(updateLoadingState('hidden'))
        if (entityType === ENTITY_TYPES.Lead) {
          dispatch(decrementLeadServicesCount({ leadId: entityId }))
          if (detailId === entityId) dispatch(getLeadDetail(entityId))
          dispatch(getLeadValue(entityId))
        } else {
          dispatch(decrementOppServicesCount({ oppId: entityId }))
          if (detailId === entityId) dispatch(getOpportunityDetail(entityId))
          dispatch(getOpportunityValue(entityId))
        }
        toast.success('Service deleted')
        // Display the optional toast message if any Draft agreements were updated in the process
        if (result.payload.draftAgreementsUpdated) {
          toast.success(
            'Service has been removed from any Draft and In Progress agreements.',
          )

          //Update the Agreements count for the Lead entity IF draft agreements were deleted in the process of deleting the last Lead product
          if (
            entityType === ENTITY_TYPES.Lead &&
            result.payload.deletedDraftAgreementsCount > 0
          )
            dispatch(
              updateDeletedLeadAgreementsCount({
                leadId: entityId,
                deletedAgreementsCount:
                  result.payload.deletedDraftAgreementsCount,
              }),
            )
        }
      } else {
        dispatch(updateLoadingState('hidden'))
        toast.error('Failed to delete service')
      }
    }
  }, [localMode, dispatch, id, entityType, entityId, detailId])

  const showDeleteDialog = useCallback(
    () => setDialog(disableDelete ? 'noDelete' : 'delete'),
    [disableDelete, setDialog],
  )

  const hideDeleteDialog = useCallback(() => setDialog(undefined), [setDialog])

  return {
    handleAccordionChange,
    handleEditClick,
    showDeleteDialog,
    hideDeleteDialog,
    handleDeleteClick,
  }
}
